/* Category search selects the existing feed controls; it never changes the feed query while typing. */
(() => {
  "use strict";
  function init() {
    const strip = document.getElementById("category-strip");
    const trigger = document.getElementById("category-search-toggle");
    const dialog = document.getElementById("category-search-dialog");
    if (!strip || !trigger || !dialog) return;
    const input = dialog.querySelector("input");
    const results = dialog.querySelector(".category-search-results");
    const empty = dialog.querySelector(".category-search-empty");
    const status = dialog.querySelector(".category-search-status");
    const choices = dialog.querySelector(".category-choices");
    const scope = document.getElementById("feed-scope");
    const order = document.getElementById("feed-order");
    const normalize = value => value.normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

    // Home ships its final control row in the HTML. Filtering uses the same sheet at
    // every width, without moving the feed controls after the first paint.
    function renderChoices() {
      if (!choices) return;
      choices.replaceChildren();
      choices.hidden = false;
      const visible = button => !button.hidden && !button.closest("[hidden]");
      const groups = [
        ["Show", scope ? Array.from(scope.querySelectorAll("button[data-view]")).filter(visible) : []],
        ["Order", order ? Array.from(order.querySelectorAll("button[data-sort]")).filter(visible) : []],
      ];
      for (const [title, sources] of groups) {
        if (!sources.length) continue;
        const group = document.createElement("div");
        group.className = "category-choice-group";
        group.setAttribute("role", "group");
        group.setAttribute("aria-label", title);
        const heading = document.createElement("p");
        heading.className = "category-choice-title";
        heading.textContent = title;
        const row = document.createElement("div");
        row.className = "category-choice-row";
        for (const source of sources) {
          const choice = document.createElement("button");
          choice.type = "button";
          choice.className = "category-choice";
          choice.textContent = source.textContent.trim();
          const active = source.classList.contains("is-active") || source.getAttribute("aria-pressed") === "true";
          choice.setAttribute("aria-pressed", String(active));
          choice.addEventListener("click", () => { dialog.close(); source.click(); });
          row.append(choice);
        }
        group.append(heading, row);
        choices.append(group);
      }
    }
    const categories = Array.from(strip.querySelectorAll("button[data-filter]"), button => ({
      button, key: button.dataset.filter,
      label: button.dataset.filter === "all" ? "All categories" : button.textContent.trim(),
      terms: normalize(button.textContent + " " + button.dataset.filter),
    }));
    const filterLabel = trigger.querySelector(".category-filter-label");
    function updateFilterLabel() {
      const parts = [];
      const view = document.body.dataset.view;
      if (["topics", "unseen"].includes(view)) {
        const source = scope?.querySelector(`button[data-view="${view}"]`);
        if (source) parts.push(source.textContent.trim());
      }
      parts.push(...categories.filter(category => category.key !== "all"
        && category.button.classList.contains("is-active")).map(category => category.label));
      const selectedOrder = order?.querySelector('[data-sort]:not([data-sort="new"])[aria-pressed="true"]');
      if (selectedOrder) parts.push(selectedOrder.textContent.trim());
      const summary = parts.length ? `Filter: ${parts.join("; ")}` : "Filter";
      const compact = parts.length ? parts[0] + (parts.length > 1 ? ` +${parts.length - 1}` : "") : "Filter";
      if (filterLabel && filterLabel.textContent !== compact) filterLabel.textContent = compact;
      if (trigger.getAttribute("aria-label") !== summary) trigger.setAttribute("aria-label", summary);
      if (trigger.title !== summary) trigger.title = summary;
      trigger.dataset.active = String(parts.length > 0);
    }
    // Reflect the authoritative controls, including restored filters, keyboard
    // navigation, empty-state reset, sort changes and changes outside this sheet.
    const filterObserver = new MutationObserver(updateFilterLabel);
    filterObserver.observe(strip, { subtree: true, attributes: true, attributeFilter: ["class", "aria-pressed"] });
    if (order) filterObserver.observe(order, { subtree: true, attributes: true, attributeFilter: ["aria-pressed"] });
    filterObserver.observe(document.body, { attributes: true, attributeFilter: ["data-view"] });
    updateFilterLabel();
    let frame = null;
    function updateEdges() {
      frame = null;
      const limit = Math.max(0, strip.scrollWidth - strip.clientWidth);
      strip.classList.toggle("can-scroll-back", strip.scrollLeft > 2 && limit > 2);
      strip.classList.toggle("can-scroll-forward", strip.scrollLeft < limit - 2);
    }
    function scheduleEdges() {
      if (frame === null) frame = requestAnimationFrame(updateEdges);
    }
    function select(category) {
      dialog.close();
      category.button.click();
      scheduleEdges();
    }
    function renderResults() {
      const term = normalize(input.value);
      const matches = categories.filter(category => !term || normalize(category.label).includes(term) || category.terms.includes(term));
      results.replaceChildren();
      for (const category of matches) {
        const result = document.createElement("button");
        result.type = "button";
        result.className = "category-search-result";
        result.dataset.category = category.key;
        const selected = category.button.classList.contains("is-active");
        result.setAttribute("aria-pressed", String(selected));
        const icon = document.createElement("span");
        icon.className = "category-result-icon";
        icon.setAttribute("aria-hidden", "true");
        const originalIcon = category.button.querySelector("svg");
        if (originalIcon) icon.append(originalIcon.cloneNode(true));
        else icon.textContent = "#";
        const label = document.createElement("span");
        label.textContent = category.label;
        result.append(icon, label);
        if (selected) {
          const mark = document.createElement("span");
          mark.className = "category-result-selected";
          mark.setAttribute("aria-hidden", "true");
          mark.textContent = "✓";
          result.append(mark);
        }
        result.addEventListener("click", () => select(category));
        results.append(result);
      }
      empty.hidden = matches.length !== 0;
      status.textContent = `${matches.length} ${matches.length === 1 ? "category" : "categories"} found`;
    }
    trigger.addEventListener("click", () => {
      input.value = "";
      renderChoices();
      renderResults();
      dialog.showModal();
      trigger.setAttribute("aria-expanded", "true");
      input.focus({preventScroll: true});
    });
    dialog.querySelector(".category-search-close").addEventListener("click", () => dialog.close());
    dialog.addEventListener("close", () => {
      input.value = "";
      results.replaceChildren();
      status.textContent = "";
      trigger.setAttribute("aria-expanded", "false");
      trigger.focus({preventScroll: true});
    });
    dialog.addEventListener("click", event => {
      if (event.target !== dialog) return;
      const box = dialog.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
    });
    input.addEventListener("input", renderResults);
    input.addEventListener("keydown", event => {
      if (event.isComposing || event.keyCode === 229) return;
      const buttons = results.querySelectorAll("button");
      if (!buttons.length) return;
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        buttons[event.key === "ArrowDown" ? 0 : buttons.length - 1].focus();
      } else if (event.key === "Enter") {
        event.preventDefault();
        buttons[0].click();
      }
    });
    results.addEventListener("keydown", event => {
      if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
      const buttons = Array.from(results.querySelectorAll("button"));
      const index = buttons.indexOf(event.target);
      if (index < 0) return;
      event.preventDefault();
      if (event.key === "ArrowUp" && index === 0) { input.focus(); return; }
      const next = event.key === "Home" ? 0 : event.key === "End" ? buttons.length - 1
        : Math.min(buttons.length - 1, index + (event.key === "ArrowDown" ? 1 : -1));
      buttons[next].focus();
    });
    strip.addEventListener("scroll", scheduleEdges, {passive: true});
    strip.addEventListener("focusin", event => {
      const button = event.target.closest("button[data-filter]");
      if (!button) return;
      // Keyboard focus only. A tap focuses the chip too, and scrolling the strip under the finger
      // before the click moved an edge chip away from the pointer, so the tap reached nothing.
      if (!button.matches(":focus-visible")) return;
      const item = button.getBoundingClientRect(), viewport = strip.getBoundingClientRect();
      if (item.left < viewport.left + 28 || item.right > viewport.right - 28) {
        strip.scrollBy({left: (item.left + item.right - viewport.left - viewport.right) / 2, behavior: "auto"});
      }
    });
    addEventListener("resize", scheduleEdges);
    if ("ResizeObserver" in window) new ResizeObserver(scheduleEdges).observe(strip);
    document.fonts?.ready.then(scheduleEdges);
    scheduleEdges();
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init, {once: true});
  else init();
})();
